// Nokta detayinda Muhasebe tab — masraflar listesi + ozet kart.

import { useMemo, useState } from 'react';
import { Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import {
  BosDurum,
  Buton,
  Kart,
  KartBaslik,
  KartIcerik,
  OnayDialog,
  Rozet,
  TarihAraligiSecici,
  VeriTablo,
  toast,
  type ColumnDef,
  type TarihAraligi,
} from '@emanetx/ui';

/** Yerel tarihi YYYY-MM-DD'ye çevir (UTC kaymadan). */
function yerelIso(d?: Date | null): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
import type { MasrafKategori, MasrafKaydiDetay } from '@emanetx/sdk';
import { useKarlilik, useMasraflar, useMasrafSil } from './api.js';
import { MASRAF_KATEGORI_ETIKETI, MasrafDialog } from './masraf-dialog.js';

function sonAltiAy(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setMonth(s.getMonth() - 5);
  s.setDate(1);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export function NoktaMuhasebePaneli({
  noktaId,
  noktaKod,
  noktaAd,
}: {
  noktaId: string;
  noktaKod: string;
  noktaAd: string;
}) {
  const [dialogAcik, setDialogAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState<MasrafKaydiDetay | null>(null);
  const [silinecek, setSilinecek] = useState<MasrafKaydiDetay | null>(null);
  const [sayfa, setSayfa] = useState(1);
  const [aralik, setAralik] = useState<TarihAraligi>(sonAltiAy);

  const baslangicIso = yerelIso(aralik.baslangic);
  const bitisIso = yerelIso(aralik.bitis);

  const donemSorgusu = useMemo(() => {
    const b = aralik.baslangic ?? new Date();
    const e = aralik.bitis ?? new Date();
    return {
      yilBaslangic: b.getFullYear(),
      ayBaslangic: b.getMonth() + 1,
      yilBitis: e.getFullYear(),
      ayBitis: e.getMonth() + 1,
    };
  }, [aralik]);

  const liste = useMasraflar({
    noktaId,
    sayfa,
    boyut: 50,
    ...(baslangicIso && { baslangic: baslangicIso }),
    ...(bitisIso && { bitis: bitisIso }),
  });

  const karlilik = useKarlilik({ ...donemSorgusu, noktaId });
  const sil = useMasrafSil();

  const noktaOzeti = karlilik.data?.noktalar.find((n) => n.noktaId === noktaId);

  const silOnayla = async () => {
    if (!silinecek) return;
    try {
      await sil.mutateAsync(silinecek.id);
      toast.success('Masraf silindi');
      setSilinecek(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const kolonlar = useMemo<ColumnDef<MasrafKaydiDetay, unknown>[]>(
    () => [
      {
        accessorKey: 'tarih',
        header: 'Tarih',
        meta: { etiket: 'Tarih' },
        cell: ({ row }) => (
          <span className="text-fume-700 dark:text-fume-200 font-mono text-xs">
            {row.original.tarih}
          </span>
        ),
      },
      {
        accessorKey: 'kategori',
        header: 'Kategori',
        meta: { etiket: 'Kategori' },
        cell: ({ row }) => (
          <Rozet ton="mavi" className="text-[10px]">
            {MASRAF_KATEGORI_ETIKETI[row.original.kategori as MasrafKategori]}
          </Rozet>
        ),
      },
      {
        accessorKey: 'tutar',
        header: 'Tutar',
        meta: { etiket: 'Tutar' },
        cell: ({ row }) => (
          <span className="font-mono text-sm font-semibold text-red-600 dark:text-red-400">
            {Number(row.original.tutar).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
          </span>
        ),
      },
      {
        accessorKey: 'aciklama',
        header: 'Açıklama',
        meta: { etiket: 'Açıklama' },
        cell: ({ row }) => (
          <span
            className="text-fume-600 dark:text-fume-300 block max-w-[280px] truncate text-xs"
            title={row.original.aciklama ?? ''}
          >
            {row.original.aciklama ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'fis',
        header: 'Fiş No',
        meta: { etiket: 'Fiş No' },
        cell: ({ row }) =>
          row.original.fis ? (
            <code className="text-fume-500 font-mono text-[11px]">{row.original.fis}</code>
          ) : (
            <span className="text-fume-400 text-xs">—</span>
          ),
      },
      {
        accessorKey: 'olusturmaTarihi',
        header: 'Eklendi',
        meta: { etiket: 'Eklendi' },
        cell: ({ row }) => (
          <div className="flex flex-col text-xs">
            <span className="text-fume-700 dark:text-fume-200">
              {row.original.olusturan.adSoyad}
            </span>
            <span className="text-fume-400 text-[10px]">
              {new Date(row.original.olusturmaTarihi).toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'short',
              })}
            </span>
          </div>
        ),
      },
      {
        id: 'sonDuzenleyen',
        header: 'Son düzenleyen',
        meta: { etiket: 'Son düzenleyen' },
        cell: ({ row }) =>
          row.original.sonDuzenleyen ? (
            <div className="flex flex-col text-xs">
              <span className="text-fume-700 dark:text-fume-200">
                {row.original.sonDuzenleyen.adSoyad}
              </span>
              <span className="text-fume-400 text-[10px]">
                {new Date(row.original.guncellemeTarihi).toLocaleString('tr-TR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          ) : (
            <span className="text-fume-400 text-xs">—</span>
          ),
      },
      {
        id: 'aksiyonlar',
        header: '',
        enableSorting: false,
        meta: { etiket: 'Aksiyonlar' },
        cell: ({ row }) => (
          <div className="flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDuzenlenen(row.original);
                setDialogAcik(true);
              }}
              className="text-fume-400 hover:text-marka-600 p-1"
              aria-label="Düzenle"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSilinecek(row.original);
              }}
              className="text-fume-400 p-1 hover:text-red-600"
              aria-label="Sil"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      {/* Ust ozet kart */}
      <div className="grid gap-3 sm:grid-cols-4">
        <OzetKart etiket="Gelir" tutar={noktaOzeti?.gelirBrut ?? '0.00'} renk="emerald" />
        <OzetKart etiket="Ceza" tutar={noktaOzeti?.ceza ?? '0.00'} renk="amber" />
        <OzetKart etiket="Gider" tutar={noktaOzeti?.gider ?? '0.00'} renk="red" negatif />
        <OzetKart
          etiket="Net Kâr"
          tutar={noktaOzeti?.net ?? '0.00'}
          renk={noktaOzeti && Number(noktaOzeti.net) >= 0 ? 'marka' : 'red'}
          guclu
        />
      </div>

      <Kart>
        <KartBaslik>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">Masraflar</h2>
              <p className="text-fume-500 text-xs">
                {baslangicIso} → {bitisIso} · {liste.data?.toplam ?? 0} kayıt
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <TarihAraligiSecici
                deger={aralik}
                onDegisim={(a) => {
                  setAralik(a);
                  setSayfa(1);
                }}
                etiketYok
                className="h-9 text-xs"
              />
              <Buton
                boyut="kucuk"
                onClick={() => {
                  setDuzenlenen(null);
                  setDialogAcik(true);
                }}
              >
                <Plus size={13} /> Yeni Masraf
              </Buton>
            </div>
          </div>
        </KartBaslik>
        <KartIcerik>
          <VeriTablo
            veri={liste.data?.veriler ?? []}
            kolonlar={kolonlar}
            toplam={liste.data?.toplam}
            sayfa={sayfa}
            boyut={50}
            onSayfa={setSayfa}
            yukleniyor={liste.isLoading}
            yenileniyor={liste.isFetching}
            onYenile={() => void liste.refetch()}
            bosDurum={
              <BosDurum
                ikon={Receipt}
                baslik="Henüz masraf girilmedi"
                aciklama="Kira, internet, elektrik vb. masrafları ekle, karlılık raporunda görünür."
                aksiyon={
                  <Buton
                    boyut="kucuk"
                    onClick={() => {
                      setDuzenlenen(null);
                      setDialogAcik(true);
                    }}
                  >
                    <Plus size={13} /> İlk Masrafı Ekle
                  </Buton>
                }
              />
            }
          />
        </KartIcerik>
      </Kart>

      <MasrafDialog
        acik={dialogAcik}
        onDegisim={(v) => {
          setDialogAcik(v);
          if (!v) setDuzenlenen(null);
        }}
        noktaId={noktaId}
        noktaKod={noktaKod}
        noktaAd={noktaAd}
        duzenlenen={duzenlenen}
      />

      <OnayDialog
        acik={silinecek !== null}
        onDegisim={(v) => !v && setSilinecek(null)}
        baslik="Masrafı sil"
        aciklama={
          silinecek
            ? `${MASRAF_KATEGORI_ETIKETI[silinecek.kategori]} · ${Number(silinecek.tutar).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺ (${silinecek.tarih}) silinsin mi? Bu işlem geri alınamaz.`
            : undefined
        }
        onayMetni="Evet, sil"
        iptalMetni="Vazgeç"
        tur="tehlike"
        onOnay={silOnayla}
      />
    </div>
  );
}

function OzetKart({
  etiket,
  tutar,
  renk,
  negatif,
  guclu,
}: {
  etiket: string;
  tutar: string;
  renk: 'emerald' | 'red' | 'amber' | 'marka';
  negatif?: boolean;
  guclu?: boolean;
}) {
  const renkSinif = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    red: 'text-red-600 dark:text-red-400',
    amber: 'text-amber-600 dark:text-amber-400',
    marka: 'text-marka-600 dark:text-marka-400',
  }[renk];
  const sayi = Number(tutar);
  return (
    <div className="border-fume-100 dark:border-fume-800 dark:bg-fume-900 rounded-xl border bg-white p-4">
      <p className="text-fume-500 dark:text-fume-400 text-[11px] font-semibold uppercase tracking-wide">
        {etiket}
      </p>
      <p
        className={`mt-1 font-mono ${guclu ? 'text-2xl font-bold' : 'text-lg font-semibold'} ${renkSinif}`}
      >
        {negatif && sayi > 0 ? '−' : ''}
        {sayi.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
      </p>
    </div>
  );
}
